import { create } from 'zustand';
import { conditionalDevtools } from './middleware/conditionalDevtools';
import type { GameDate } from '../types/events';
import { createSyncMiddleware, type WithSyncControls } from './middleware/syncMiddleware';
import { DataMigrationService } from '../services/DataMigrationService';
import type { DataVersion } from '../utils/dataMigration';
import { GAME_CONSTANTS } from '../config/gameConstants';

export interface GameSettings {
  soundEnabled: boolean;
  musicVolume: number;
  sfxVolume: number;
  notificationsEnabled: boolean;
  autoSaveEnabled: boolean;
  autoSaveInterval: number; // minutes
  simulationPaused: boolean;
  debugMode: boolean;
}

export interface GameStatistics {
  totalRevenue: number;
  totalCosts: number;
  totalProfit: number;
  productsLaunched: number;
  productsDiscontinued: number;
  totalEmployees: number;
  totalResearchCompleted: number;
  gameStartDate: GameDate;
  totalPlayTime: number; // seconds
}

interface GameStore extends WithSyncControls {
  // State
  gameDate: GameDate;
  isPaused: boolean;
  simulationSpeed: number;
  settings: GameSettings;
  statistics: GameStatistics;
  
  // Actions
  updateGameDate: (date: GameDate) => void;
  pauseGame: () => void;
  resumeGame: () => void;
  setSimulationSpeed: (speed: number) => void;
  updateSettings: (settings: Partial<GameSettings>) => void;
  updateStatistics: (statistics: Partial<GameStatistics>) => void;
  incrementStatistic: (key: keyof GameStatistics, amount: number) => void;
  resetGame: () => void;
}

const defaultSettings: GameSettings = {
  soundEnabled: true,
  musicVolume: 0.5,
  sfxVolume: 0.7,
  notificationsEnabled: true,
  autoSaveEnabled: true,
  autoSaveInterval: 5,
  simulationPaused: false,
  debugMode: false,
};

const defaultStatistics: GameStatistics = {
  totalRevenue: 0,
  totalCosts: 0,
  totalProfit: 0,
  productsLaunched: 0,
  productsDiscontinued: 0,
  totalEmployees: 0,
  totalResearchCompleted: 0,
  gameStartDate: GAME_CONSTANTS.time.startingDate,
  totalPlayTime: 0,
};

// Current version of the game store schema
const GAME_STORE_VERSION: DataVersion = { major: 1, minor: 0, patch: 0 };

// Partialize function to select only persistent data
const partializeGameStore = (state: GameStore) => ({
  // Persist core game state
  gameDate: state.gameDate,
  settings: state.settings,
  statistics: state.statistics,
  // Exclude temporary UI state
  // isPaused and simulationSpeed are excluded as they're session-specific
});

// Custom sync configuration for game store
const gameSyncConfig = {
  routing: {
    rules: [
      // Game date updates are frequent but small - keep in localStorage
      {
        match: (path) => path === 'gameDate',
        target: 'localStorage' as const,
        priority: 100
      },
      // Settings are accessed frequently - keep in localStorage
      {
        match: (path) => path === 'settings',
        target: 'localStorage' as const,
        priority: 90
      },
      // Statistics can grow over time - consider IndexedDB for long games
      {
        match: (path, value, metadata) => 
          path === 'statistics' && metadata.size > 10 * 1024, // 10KB threshold
        target: 'indexedDB' as const,
        priority: 80
      },
      // Default to localStorage for quick access
      {
        match: () => true,
        target: 'localStorage' as const,
        priority: 0
      }
    ],
    defaultTarget: 'localStorage' as const
  },
  behavior: {
    debounceDelay: 1000, // 1 second debounce for game date updates
    enableAutoSync: true,
    syncOnReconnect: true,
    maxRetries: 3
  },
  hooks: {
    beforeSync: (data) => {
      // If data is not a full state object (e.g., just a field being synced), return as-is
      if (!data || typeof data !== 'object') {
        return data;
      }

      // Clean up data before sync
      const cleaned = { ...data };
      // Ensure statistics are numbers
      if (cleaned.statistics) {
        Object.keys(cleaned.statistics).forEach(key => {
          if (typeof cleaned.statistics[key] === 'string') {
            cleaned.statistics[key] = Number(cleaned.statistics[key]) || 0;
          }
        });
      }
      return cleaned;
    },
    afterSync: (result) => {
      if (result.success) {
        // console.debug('[GameStore] Synced successfully to', result.target);
      }
    },
    onError: (error) => {
      console.error('[GameStore] Sync error:', error.message);
    }
  },
  monitoring: {
    trackMetrics: true,
    reportInterval: 60000 // Report metrics every minute
  }
};

// Initialize migration service for game store
const migrationService = DataMigrationService.getInstance();

// Register migration schemas
migrationService.registerMigrationSchemas('gameStore', [
  {
    version: { major: 1, minor: 0, patch: 0 },
    description: 'Initial game store schema',
    up: async (data) => data,
    down: async (data) => data
  }
]);

export const useGameStore = create<GameStore>()(
  conditionalDevtools(
    createSyncMiddleware(
      'gameStore',
      gameSyncConfig
    )(
      (set) => ({
      // Initial state
      gameDate: GAME_CONSTANTS.time.startingDate,
      isPaused: true, // Start paused by default
      simulationSpeed: 1,
      settings: defaultSettings,
      statistics: defaultStatistics,

      // Actions
      updateGameDate: (date) =>
        set(() => ({ gameDate: date }), false, 'updateGameDate'),

      pauseGame: () =>
        set(
          (state) => ({
            isPaused: true,
            settings: { ...state.settings, simulationPaused: true },
          }),
          false,
          'pauseGame'
        ),

      resumeGame: () =>
        set(
          (state) => ({
            isPaused: false,
            settings: { ...state.settings, simulationPaused: false },
          }),
          false,
          'resumeGame'
        ),

      setSimulationSpeed: (speed) =>
        set(
          () => ({
            simulationSpeed: Math.max(0, Math.min(10, speed)), // Clamp between 0 and 10
          }),
          false,
          'setSimulationSpeed'
        ),

      updateSettings: (settings) =>
        set(
          (state) => ({
            settings: { ...state.settings, ...settings },
          }),
          false,
          'updateSettings'
        ),

      updateStatistics: (statistics) =>
        set(
          (state) => ({
            statistics: { ...state.statistics, ...statistics },
          }),
          false,
          'updateStatistics'
        ),

      incrementStatistic: (key, amount) =>
        set(
          (state) => ({
            statistics: {
              ...state.statistics,
              [key]: (state.statistics[key] as number) + amount,
            },
          }),
          false,
          `incrementStatistic:${key}`
        ),

      resetGame: () =>
        set(
          {
            gameDate: GAME_CONSTANTS.time.startingDate,
            isPaused: true, // Start paused after reset
            simulationSpeed: 1,
            settings: defaultSettings,
            statistics: defaultStatistics,
          },
          false,
          'resetGame'
        ),
    })
    ),
    {
      name: 'game-store',
      serialize: {
        options: {
          map: true,
          set: true,
          date: true,
          error: true,
          regex: true,
          undefined: true,
          nan: true,
          infinity: true,
          symbol: false
        }
      }
    }
  )
);

// Helper to persist only specific data
export const persistGameState = () => {
  const state = useGameStore.getState();
  const persistentData = partializeGameStore(state);
  return persistentData;
};

// Helper to load persisted game state
export const loadPersistedGameState = async () => {
  try {
    // Trigger migration if needed
    await migrationService.migrate('gameStore', GAME_STORE_VERSION, {
      createBackup: true,
      validateAfter: true
    });
    
    // Load persisted data
    const persistedData = await migrationService.getCurrentVersion('gameStore');
    return persistedData;
  } catch (error) {
    console.error('[GameStore] Failed to load persisted state:', error);
    return null;
  }
};

// Selectors
export const selectGameDate = (state: GameStore) => state.gameDate;
export const selectIsPaused = (state: GameStore) => state.isPaused;
export const selectSimulationSpeed = (state: GameStore) => state.simulationSpeed;
export const selectSettings = (state: GameStore) => state.settings;
export const selectStatistics = (state: GameStore) => state.statistics;

// Computed selectors
export const selectFormattedDate = (state: GameStore) => {
  const { day, month, year } = state.gameDate;
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return `${months[month - 1]} ${day}, ${year}`;
};

export const selectNetProfit = (state: GameStore) => {
  return state.statistics.totalRevenue - state.statistics.totalCosts;
};